import classnames from 'classnames';
import { map } from 'ramda';
import { noop } from 'ramda-adjunct';
import React, { FC, Ref, useCallback, useMemo } from 'react';
import { SmartGridItem } from '../../../components/layouts/SmartGrid/SmartGridItem';
import { useGetItemPrice } from '../../../utils/hooks/useGetItemPrice';
import { MediaManagerSDKFile } from '../../../utils/media-manager-sdk';
import { isFolder, isItemFavorite } from '../../../utils/media-manager-sdk/items';
import { memoize } from '../../../utils/memoize';
import { useWrapperSize } from '../../FilesManager/SmartGrid/hooks/useWrapperSize';
import { createCalculateThumbMinWidth, splitItemsOnRows } from '../../FilesManager/SmartGrid/utils';
import styles from './HomeSlider.scss';
import { IOuter } from './HomeSlider.types';
import { HomeSliderArrows } from './HomeSliderArrows';
import { useHomeSliderControl } from './hooks/useHomeSliderControl';

const calculateStyles = memoize((minWidth) => ({ minWidth }));

export const HomeSlider: FC<IOuter> = ({
  items,
  isItemSelected,
  selectedItems,
  publicMediaCurrentCategory,
  isSecure,
  onItemClick,
  openExpandedView,
  buyItem,
  beginDrag,
  onItemRightClick,
  onNavLeftClick,
  onNavRightClick,
  dataHook,
  onItemDoubleClick,
  onFolderDoubleClick = noop,
  currentFolder,
}) => {
  const [wrapperSize, wrapperRef] = useWrapperSize();
  const [contentSize, contentRef] = useWrapperSize();

  const { getItemPrice } = useGetItemPrice();

  const shouldShowArrows = useMemo(() => {
    return wrapperSize.width < contentSize.width;
  }, [wrapperSize, contentSize]);

  const { onScrollWrapper, onScrollRight, onScrollLeft, showRightArrow, showLeftArrow, scrollingWrapper } =
    useHomeSliderControl(items);

  const sliderLeftClick = useCallback(() => {
    onNavLeftClick();
    onScrollLeft();
  }, [onNavLeftClick, onScrollLeft]);

  const sliderRightClick = useCallback(() => {
    onNavRightClick();
    onScrollRight();
  }, [onNavRightClick, onScrollRight]);

  const calculateThumbMinWidth = useCallback(createCalculateThumbMinWidth(getItemPrice), [getItemPrice]);

  const itemRows = useMemo(
    () =>
      splitItemsOnRows(
        items as MediaManagerSDKFile[],
        { rowWidth: Infinity, rowHeight: 174, itemsGap: 6 },
        calculateThumbMinWidth,
      ),
    [items, calculateThumbMinWidth],
  );

  return (
    <div className={styles.content} ref={wrapperRef as Ref<HTMLDivElement>} data-hook={dataHook}>
      <div
        className={styles.scrollingWrapper}
        ref={scrollingWrapper}
        data-hook={`${dataHook}-items`}
        onScroll={onScrollWrapper}
      >
        <div ref={contentRef as Ref<HTMLDivElement>} className={styles.scrollingContent}>
          {map((item) => {
            const { id } = item;
            const isEditing = false;
            const isSelected = isItemSelected(id) && !isEditing;
            const isCut = false;
            const thumbMinWidth = calculateThumbMinWidth(item);
            const handleDoubleClick = isFolder(item) ? onFolderDoubleClick : onItemDoubleClick;

            return (
              <SmartGridItem
                key={item.id}
                style={calculateStyles(item.smartGridSize.width)}
                thumbMinWidth={thumbMinWidth}
                className={styles.file}
                item={item as MediaManagerSDKFile}
                selectedItems={selectedItems}
                selected={isSelected}
                publicMediaCurrentCategory={publicMediaCurrentCategory}
                isCut={isCut}
                isEditing={isEditing}
                isSecure={isSecure}
                onClick={onItemClick}
                onDoubleClick={handleDoubleClick}
                openExpandedView={openExpandedView}
                isFavorite={isItemFavorite(item)}
                buyItem={buyItem}
                beginDrag={beginDrag}
                onContextMenu={onItemRightClick}
                alwaysDisplayPrice
                currentFolder={currentFolder}
              />
            );
          }, itemRows[0])}
        </div>
      </div>

      <div className={classnames(styles.arrowsHolder, { [styles.hideArrows]: !shouldShowArrows })}>
        <HomeSliderArrows
          sliderLeftClick={sliderLeftClick}
          sliderRightClick={sliderRightClick}
          showRightArrow={showRightArrow}
          showLeftArrow={showLeftArrow}
        />
      </div>
    </div>
  );
};
